import React, { useState, useEffect } from "react";
import { icons } from "../../../Assets/Icons/icons";
import { useClickOutside } from "../../Hooks/useClickOutside";
import { Globe } from "lucide-react";

const CategoryDetailsModal = ({ isOpen, onClose, category }) => {
  const [animate, setAnimate] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState("en");
  
  // Language configuration
  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "hi", name: "Hindi", flag: "🇮🇳" },
    { code: "es", name: "Spanish", flag: "🇪🇸" },
    { code: "zh", name: "Chinese", flag: "🇨🇳" }
  ];

  const domNode = useClickOutside(() => handleClose());

  // Helper to get multilingual value
  const getMultilingualValue = (value, lang) => {
    if (!value) return "";
    
    if (typeof value === "string") {
      return value;
    }
    
    if (typeof value === "object") {
      const fallback = Object.values(value)[0] || "";
      return value[lang] || value[lang + "Name"] || fallback;
    }
    
    return "";
  };

  // Get name in current language
  const getCurrentName = () => {
    return getMultilingualValue(category?.name, activeLanguage) || "Unnamed Category";
  };

  // Get description in current language
  const getCurrentDescription = () => {
    return getMultilingualValue(category?.description, activeLanguage) || "No description provided";
  };

  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      onClose();
    }, 250);
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
    }
  }, [isOpen]);

  // Conditional render AFTER hooks
  if (!isOpen || !category) return null;

  return (
    <section
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
        animate ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        ref={domNode}
        className={`bg-white rounded-2xl shadow-xl w-[90%] md:w-[700px] max-h-[90vh] overflow-y-auto transform transition-all duration-300 ${
          animate ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-philosopher text-primaryTextColor">
              Category Details
            </h2>
            <p className="text-sm text-gray-500">View category information in different languages</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-red-500 text-2xl p-2"
          >
            {icons.closeIcon || "×"}
          </button>
        </div>

        {/* Language Selector */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">View Content in:</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setActiveLanguage(lang.code)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                    activeLanguage === lang.code
                      ? "bg-blue-100 text-blue-700 border border-blue-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Current Language Display */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">
                {languages.find(l => l.code === activeLanguage)?.flag}
              </span>
              <span className="text-sm font-medium text-blue-700">
                {languages.find(l => l.code === activeLanguage)?.name} View
              </span>
            </div>
            
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-1">Category Name:</h3>
                <h2 className="text-xl font-philosopher text-primaryTextColor">
                  {getCurrentName()}
                </h2>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-1">Description:</h3>
                <p className="text-gray-600 leading-relaxed">
                  {getCurrentDescription()}
                </p>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="w-full h-64 rounded-lg overflow-hidden border border-gray-200">
            <img
              src={category.imgUrl}
              alt={getCurrentName()}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Icon (if available) */}
          {category.catIconUrl && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Category Icon:</h3>
              <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                <img
                  src={category.catIconUrl}
                  alt={`${getCurrentName()} Icon`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          {/* All Languages Preview */}
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Available in All Languages:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {languages.map((lang) => {
                const langName = getMultilingualValue(category.name, lang.code);
                const langDesc = getMultilingualValue(category.description, lang.code);
                return (
                  <div
                    key={lang.code}
                    className={`p-4 rounded-lg border ${
                      activeLanguage === lang.code
                        ? "border-blue-300 bg-blue-50"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">{lang.flag}</span>
                      <span className="text-sm font-medium text-gray-700">{lang.name}</span>
                      {lang.code === "en" && (
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                          Default
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs text-gray-500">Name:</span>
                        <p className="font-medium text-gray-800 truncate" title={langName}>
                          {langName || "Not available"}
                        </p>
                      </div>
                      
                      <div>
                        <span className="text-xs text-gray-500">Description:</span>
                        <p className="text-sm text-gray-600 line-clamp-2" title={langDesc}>
                          {langDesc || "No description"}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Technical Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">Category ID:</h3>
              <p className="text-sm text-gray-600 font-mono bg-gray-50 p-2 rounded">
                {category._id}
              </p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">Sequence Number:</h3>
              <p className="text-lg font-medium text-blue-600">
                {category.sequence || 0}
              </p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">Created At:</h3>
              <p className="text-sm text-gray-600">
                {new Date(category.createdAt).toLocaleDateString()} at {new Date(category.createdAt).toLocaleTimeString()}
              </p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">Last Updated:</h3>
              <p className="text-sm text-gray-600">
                {new Date(category.updatedAt).toLocaleDateString()} at {new Date(category.updatedAt).toLocaleTimeString()}
              </p>
            </div>
          </div>

          {/* Subcategories Count (if available) */}
          {category.subcategories && category.subcategories.length > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Subcategories ({category.subcategories.length}):</h3>
              <div className="flex flex-wrap gap-2">
                {category.subcategories.slice(0, 5).map((sub, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full"
                    title={getMultilingualValue(sub.name, activeLanguage)}
                  >
                    {getMultilingualValue(sub.name, activeLanguage)}
                  </span>
                ))}
                {category.subcategories.length > 5 && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                    +{category.subcategories.length - 5} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end sticky bottom-0 bg-white">
          <button
            onClick={handleClose}
            className="px-6 py-2.5 bg-[#b07d17] text-white rounded-lg hover:bg-[#8c6312] transition-all font-medium"
          >
            Close Details
          </button>
        </div>
      </div>
    </section>
  );
};

export default CategoryDetailsModal;








// import React, { useState, useEffect } from "react";
// import { icons } from "../../../Assets/Icons/icons";
// import { useClickOutside } from "../../Hooks/useClickOutside";

// const CategoryDetailsModal = ({ isOpen, onClose, category }) => {
//   const [animate, setAnimate] = useState(false);
//   const domNode = useClickOutside(() => handleClose());

//   const handleClose = () => {
//     setAnimate(false);
//     setTimeout(() => {
//       onClose();
//     }, 250);
//   };

//   useEffect(() => {
//     if (isOpen) {
//       setTimeout(() => setAnimate(true), 10);
//     }
//   }, [isOpen]);

//   // Conditional render AFTER hooks
//   if (!isOpen || !category) return null;

//   return (
//     <section
//       className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
//         animate ? "opacity-100" : "opacity-0"
//       }`}
//     >
//       <div
//         ref={domNode}
//         className={`bg-white rounded-2xl shadow-xl w-[90%] md:w-[600px] overflow-hidden transform transition-all duration-300 ${
//           animate ? "scale-100 opacity-100" : "scale-95 opacity-0"
//         }`}
//       >
//         {/* Header */}
//         <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
//           <h2 className="text-xl font-philosopher text-primaryTextColor">
//             {category.name}
//           </h2>
//           <button
//             onClick={handleClose}
//             className="text-gray-500 hover:text-red-500 text-2xl"
//           >
//             {icons.closeIcon || "×"}
//           </button>
//         </div>

//         {/* Body */}
//         <div className="p-6 space-y-5">
//           <div className="w-full h-56 rounded-lg overflow-hidden border">
//             <img
//               src={category.imgUrl}
//               alt={category.name}
//               className="w-full h-full object-cover"
//             />
//           </div>

//           <div>
//             <h4 className="text-primaryTextColor font-philosopher mb-1">Description</h4>
//             <p className="text-gray-500 text-sm leading-relaxed">
//               {category.description || "No description provided."}
//             </p>
//           </div>

//           <div className="flex justify-between text-sm text-gray-400">
//             <span>Category ID: {category._id}</span>
//             <span>
//               Created: {new Date(category.createdAt).toLocaleDateString()}
//             </span>
//           </div>
//         </div>

//         {/* Footer */}
//         <div className="px-6 py-3 border-t flex justify-end">
//           <button
//             onClick={handleClose}
//             className="px-4 py-2 bg-[#b07d17] text-white rounded-lg hover:bg-[#8c6312] transition-all"
//           >
//             Close
//           </button>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default CategoryDetailsModal;
